import React, { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { useRouter } from "next/router";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import ContactUsComplaintsAndEscalations from "../../components/contact-us-complaints-and-escalations/ContactUsComplaintsAndEscalations";
import MoreFromUs from "../../components/more-from-us/more-from-us";
import Solution from "../../components/solution/Solution";
import analyticsTrigger from "../../utils/analytics/trigger/analyticsTrigger";
import { getOtherAreasContactDetails } from "../../utils/data/getContactDetails";
import getSolutionTree from "../../utils/data/getSolutionTree";
import scrollToNode from "../../utils/scrollToNode";
import { MonetateProvider } from "../../context/MonetateContext";
import styles from "./index.module.scss";
import FAQPage from "../faq/index.page";
import contactMethods from "../../constants/contactMethods";
import InfoBanner from "@/components/info-banner/info-banner";
import { evaluateFlags, flagsClient, getDefinitions } from "@unleash/nextjs";
import FeatureFlagProvider from "../../context/FeatureFlagContext";

export const getServerSideProps = async ctx => {
  const props = {};
  try {
    props.definitions = await getDefinitions(ctx, {
      fetchOptions: {
        next: { revalidate: 15 },
        signal: AbortSignal.timeout(5000),
      },
    });
  } catch (err) {
    console.warn(err, `Failed getting feature flags`);
    props.definitions = { version: 1, features: [] };
  }
  return { props };
};

export const metadata = {
  title: `Contact Us | Customer Services`,
  description: `Contact our customer services team if you need help shopping with us`,
};

function ContactUs({ definitions }) {
  const pathname = usePathname();
  const { query } = useRouter();
  const { toggles } = evaluateFlags(definitions);
  const flags = flagsClient(toggles);
  const showMaintenanceBanner = flags.isEnabled("show_maintenance_banner");
  const showMIAWChat = flags.isEnabled("show_miaw_chat");
  const { dt } = query;
  const [otherAreasContactDetails, setOtherAreasContactDetails] = useState(null);
  const responseIds = dt ? decodeURIComponent(dt).split("-") : [];
  const decisionTreeRootSolutionId = "contactUsWithElectricalProductSupport";
  const decisionTreeSolutions = getSolutionTree(decisionTreeRootSolutionId, responseIds);
  const decisionTreeDisableSolutions = ["productStockInformationOutStockOptions"];

  const fetchOtherAreasContactDetails = async () => {
    const otherContactDetails = await getOtherAreasContactDetails();
    if (otherContactDetails) {
      setOtherAreasContactDetails(otherContactDetails);
    }
  };

  useEffect(() => {
    analyticsTrigger(
      "dataStateChange",
      pathname,
      {
        decisionTree: {
          refinement: "",
        },
      },
      "pageload",
    );
    fetchOtherAreasContactDetails();
  }, []);
  return (
    <MonetateProvider experiences={[]}>
      {showMaintenanceBanner && (
        <InfoBanner text="We are currently experiencing some issues with our customer service technology. Please contact us via telephone only" />
      )}
      <h1 className="page-heading" aria-label="contact us">
        Contact us
      </h1>
      <div className="util-container">
        <div className={styles["contact-us__decision-tree"]}>
          <TransitionGroup component={null}>
            {decisionTreeSolutions.map((solution, index) => (
              <CSSTransition
                key={solution.id}
                onEntered={(node, isAppearing) => {
                  if (!isAppearing) {
                    scrollToNode(node);
                  }
                }}
                addEndListener={(node, done) => {
                  node.addEventListener("transitionend", done, false);
                }}
                appear
                mountOnEnter
                unmountOnExit
              >
                <FeatureFlagProvider features={{ show_miaw_chat: showMIAWChat }}>
                  <Solution
                    index={index}
                    solution={solution}
                    className={styles["contact-us__decision-tree-solution"]}
                    contactMethods={contactMethods}
                    decisionTreeDisableSolutions={decisionTreeDisableSolutions}
                  />
                </FeatureFlagProvider>
              </CSSTransition>
            ))}
          </TransitionGroup>
        </div>
      </div>

      <div className="util-container">
        <div className={styles.dividerContainer}>
          <div className={styles.dividerWithText}>FAQs</div>
        </div>
        <FAQPage inContactUs />
        <div className={styles.dividerContainer}>
          {otherAreasContactDetails?.complaints ? (
            <ContactUsComplaintsAndEscalations contactDetails={otherAreasContactDetails.complaints} />
          ) : null}
        </div>
        <MoreFromUs />
      </div>
    </MonetateProvider>
  );
}

ContactUs.metadata = metadata;

export default ContactUs;
